import { groupBy } from 'lodash';
import { Logger } from 'common/logger';
import { noteQuery, restrictionQuery, workOrderQuery, catchErrorsV2 } from '../ReportsAPI';
import { createLogsListItem } from './helpers/overviewLogs.helper';
import { getSelectedItems, getTurbineIdToNameMap } from 'common/state.helper';

const defaultState = {
    notes: null,
    exceptions: null,
    restrictions: null,
    workOrdersIssued: null,
    workOrdersCompleted: null,
};

export const overviewLogs = {
    name: 'overviewLogs',
    state: defaultState,

    subscriptions: {
        setup() {
            Logger.info('overviewLogs setup');
        },
    },

    reducers: {
        resetAll() {
            return defaultState;
        },
        setNotesData(state, { notes }) {
            return {
                ...state,
                notes,
            };
        },
        setExceptionsData(state, { exceptions }) {
            return {
                ...state,
                exceptions,
            };
        },
        setRestrictionsData(state, { restrictions }) {
            return {
                ...state,
                restrictions,
            };
        },
        setWorkOrders(state, { workOrdersIssued, workOrdersCompleted }) {
            return {
                ...state,
                workOrdersIssued,
                workOrdersCompleted,
            };
        },
    },

    effects: (dispatch) => ({
        loadAll() {
            dispatch.overviewLogs.resetAll();

            catchErrorsV2(
                () => dispatch.overviewLogs.queryNotesAndExceptions(),
                () => {
                    dispatch.overviewLogs.setNotesData({ notes: null });
                    dispatch.overviewLogs.setExceptionsData({ exceptions: null });
                }
            );
            catchErrorsV2(
                () => dispatch.overviewLogs.queryRestrictions(),
                () => dispatch.overviewLogs.setRestrictionsData({ restrictions: null })
            );
            catchErrorsV2(
                () => dispatch.overviewLogs.queryWorkOrders(),
                () => dispatch.overviewLogs.setWorkOrders({ workOrdersIssued: null, workOrdersCompleted: null })
            );
        },

        async queryNotesAndExceptions(_, state) {
            const { selectedDate, selectedParkId } = getSelectedItems(state);
            const { data } = await noteQuery(selectedDate, selectedParkId);
            const entityKeyToNameMap = getTurbineIdToNameMap(state);

            // Use reduce to categorize notes and exceptions in a single pass
            const { notes, exceptions } = data.reduce(
                (acc, item) => {
                    if (item.note_type === 1 || item.note_type === 2) {
                        const logItem = createLogsListItem(
                            item,
                            item.note_type === 1 ? 'notes' : 'exceptions',
                            entityKeyToNameMap
                        );
                        if (item.note_type === 1) {
                            acc.notes.push(logItem);
                        } else if (item.note_type === 2) {
                            acc.exceptions.push(logItem);
                        }
                    }

                    return acc;
                },
                { notes: [], exceptions: [] }
            );

            // Dispatch both updates in parallel
            Promise.all([
                dispatch.overviewLogs.setNotesData({ notes }),
                dispatch.overviewLogs.setExceptionsData({ exceptions }),
            ]);
        },

        async queryRestrictions(_, state) {
            const { selectedDate, selectedParkId } = getSelectedItems(state);
            const { data } = await restrictionQuery(selectedDate, selectedParkId);
            const entityKeyToNameMap = getTurbineIdToNameMap(state);
            const list = data.map((item) => createLogsListItem(item, 'restrictions', entityKeyToNameMap));

            dispatch.overviewLogs.setRestrictionsData({ restrictions: list });
        },

        async queryWorkOrders(_, state) {
            const { selectedDate, selectedParkId } = getSelectedItems(state);
            const { data } = await workOrderQuery(selectedDate, selectedParkId, undefined, ['COMPLETED', 'ISSUED']);

            if (data.length > 0) {
                const grouped = groupBy(data, 'status');
                const entityKeyToNameMap = getTurbineIdToNameMap(state);

                const completed = grouped.COMPLETED
                    ? grouped.COMPLETED.map((item) =>
                          createLogsListItem(item, 'workOrdersCompleted', entityKeyToNameMap)
                      )
                    : [];

                const issued = grouped.ISSUED
                    ? grouped.ISSUED.map((item) => createLogsListItem(item, 'workOrdersIssued', entityKeyToNameMap))
                    : [];

                dispatch.overviewLogs.setWorkOrders({
                    workOrdersIssued: issued,
                    workOrdersCompleted: completed,
                });
            } else {
                dispatch.overviewLogs.setWorkOrders({ workOrdersIssued: [], workOrdersCompleted: [] });
            }
        },
    }),
};

export default overviewLogs;
